// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Drivers
// Driver-centric view of crew data — manage driver assignments,
// contact info, and associated trucks/zones
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Table,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconPhone, IconMail,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { DebrisCrew } from '../../types/debris';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'standby', label: 'Standby' },
  { value: 'out_of_service', label: 'Out of Service' },
];

const CREW_TYPES = [
  { value: 'knuckleboom', label: 'Knuckleboom' },
  { value: 'grapple', label: 'Grapple' },
  { value: 'self_loader', label: 'Self-Loader' },
  { value: 'dump_truck', label: 'Dump Truck' },
];

const STATUS_COLORS: Record<string, string> = {
  active: 'green', standby: 'yellow', out_of_service: 'red',
};

const EMPTY: Partial<DebrisCrew> = {
  contractorId: '', crewName: '', crewType: '', truckId: '',
  driverName: '', driverPhone: '', driverEmail: '',
  status: 'active', assignedZoneId: '', notes: '',
};

export default function Drivers() {
  const { canEdit } = useModulePermission('debris');
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    crews, contractors, trucks, zones, loadingCrews,
    loadCrews, loadContractors, loadTrucks, loadZones,
    createCrew, updateCrew, deleteCrew,
  } = useDebrisStore();

  const { loadTickets, loadLoadTickets } = useDebrisOpsStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [contractorFilter, setContractorFilter] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DebrisCrew | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisCrew | null>(null);
  const [detailTarget, setDetailTarget] = useState<DebrisCrew | null>(null);
  const [form, setForm] = useState<Partial<DebrisCrew>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCrews(); loadContractors(); loadTrucks(); loadZones(); loadLoadTickets();
  }, [loadCrews, loadContractors, loadTrucks, loadZones, loadLoadTickets]);

  const iCrews = incidentId ? crews : [];
  const iContractors = incidentId ? contractors : [];
  const iTrucks = incidentId ? trucks : [];
  const iZones = incidentId ? zones : [];

  // Only show crews that have a driver assigned
  const driversOnly = useMemo(() => iCrews.filter(c => c.driverName), [iCrews]);

  const contractorSelect = useMemo(() =>
    iContractors.map(c => ({ value: String(c.id), label: c.companyName || `#${c.id}` })),
  [iContractors]);
  const truckSelect = useMemo(() =>
    iTrucks.map(t => ({ value: String(t.id), label: `${t.truckNumber} (${t.cubicYardCapacity || '?'} CY)` })),
  [iTrucks]);
  const zoneSelect = useMemo(() =>
    iZones.map(z => ({ value: String(z.id), label: z.zoneName || z.zoneCode || `#${z.id}` })),
  [iZones]);

  const filtered = useMemo(() => {
    let list = driversOnly;
    if (statusFilter && statusFilter !== 'all') list = list.filter(c => c.status === statusFilter);
    if (contractorFilter && contractorFilter !== 'all') list = list.filter(c => c.contractorId === contractorFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.driverName.toLowerCase().includes(q) ||
        c.driverEmail.toLowerCase().includes(q) ||
        c.driverPhone.includes(q) ||
        c.crewName.toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return a.driverName.localeCompare(b.driverName);
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      const numA = Number(va); const numB = Number(vb);
      if (!isNaN(numA) && !isNaN(numB) && va !== '' && vb !== '') return (numA - numB) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [driversOnly, statusFilter, contractorFilter, search, sortField, sortDir]);

  const contractorName = (id: string) => contractors.find(c => String(c.id) === id)?.companyName ?? '';
  const truckLabel = (id: string) => { const t = trucks.find(t => String(t.id) === id); return t ? `${t.truckNumber} (${t.truckType || ''})` : ''; };
  const zoneName = (id: string) => zones.find(z => String(z.id) === id)?.zoneName ?? '';
  const setField = (key: keyof DebrisCrew, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  // Count loads for a driver/crew
  const driverLoadCount = useMemo(() => {
    const map: Record<number, { count: number; cy: number }> = {};
    const iTickets = loadTickets;
    iTickets.forEach(t => {
      const crewId = parseInt(t.crewId || '0');
      if (crewId) {
        if (!map[crewId]) map[crewId] = { count: 0, cy: 0 };
        map[crewId].count++;
        map[crewId].cy += parseFloat(t.calculatedCubicYards || '0');
      }
    });
    return map;
  }, [loadTickets, incidentId]);

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY });
    setModalOpen(true);
  };

  const openEdit = (c: DebrisCrew) => {
    setEditTarget(c);
    setForm({ ...c });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.driverName?.trim()) {
      notifications.show({ message: 'Driver name is required', color: 'yellow' });
      return;
    }
    if (!form.crewName?.trim()) {
      // Auto-generate crew name from driver name
      form.crewName = `${form.driverName}'s Crew`;
    }
    setSaving(true);
    try {
      const payload = { ...form, incidentId };
      if (editTarget) {
        await updateCrew(editTarget.id, payload);
        notifications.show({ message: 'Driver updated', color: 'green' });
      } else {
        await createCrew(payload);
        notifications.show({ message: 'Driver added', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteCrew(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.driverName} removed`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Drivers</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingCrews} onClick={() => loadCrews()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Driver</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search driver name, email, phone, crew..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.currentTarget.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
        <Select data={[{ value: 'all', label: 'All Contractors' }, ...contractorSelect]}
          value={contractorFilter ?? 'all'} onChange={v => setContractorFilter(v)} size="xs" w={200} searchable />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} driver{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('driverName')}>Driver{sortIcon('driverName')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Contact</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Contractor</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('crewName')}>Crew{sortIcon('crewName')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Truck</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Zone</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Loads</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>CY Hauled</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingCrews ? 'Loading...' : 'No drivers found'}
                </td></tr>
              )}
              {filtered.map(c => {
                const stats = driverLoadCount[c.id] || { count: 0, cy: 0 };
                return (
                  <tr key={c.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', cursor: 'pointer' }}
                    onClick={() => setDetailTarget(c)}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>{c.driverName}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <Group gap={4}>
                        {c.driverPhone && <Group gap={2}><IconPhone size={12} /><Text size="xs">{c.driverPhone}</Text></Group>}
                        {c.driverEmail && <Group gap={2}><IconMail size={12} /><Text size="xs">{c.driverEmail}</Text></Group>}
                      </Group>
                    </td>
                    <td style={{ padding: '8px 12px' }}>{c.contractorId ? contractorName(c.contractorId) : '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{c.crewName || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{c.truckId ? truckLabel(c.truckId) : '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{c.assignedZoneId ? zoneName(c.assignedZoneId) : '-'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge size="sm" color={STATUS_COLORS[c.status] ?? 'gray'}>{c.status?.replace(/_/g, ' ')}</Badge>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>
                      {stats.count}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>
                      {stats.cy > 0 ? stats.cy.toLocaleString() : '-'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      {canEdit && (
                      <Group gap={4} justify="flex-end">
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(c)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                        <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(c)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Driver Detail Modal */}
      <Modal opened={!!detailTarget} onClose={() => setDetailTarget(null)}
        title={`Driver: ${detailTarget?.driverName}`} centered size="lg">
        {detailTarget && (
          <Stack>
            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Phone</Text>
                <Text>{detailTarget.driverPhone || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Email</Text>
                <Text>{detailTarget.driverEmail || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Contractor</Text>
                <Text>{detailTarget.contractorId ? contractorName(detailTarget.contractorId) : '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Crew</Text>
                <Text>{detailTarget.crewName || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Truck</Text>
                <Text>{detailTarget.truckId ? truckLabel(detailTarget.truckId) : '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Assigned Zone</Text>
                <Text>{detailTarget.assignedZoneId ? zoneName(detailTarget.assignedZoneId) : '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Status</Text>
                <Badge color={STATUS_COLORS[detailTarget.status] ?? 'gray'}>{detailTarget.status?.replace(/_/g, ' ')}</Badge>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Total Loads / CY</Text>
                <Text fw={600}>
                  {(driverLoadCount[detailTarget.id]?.count ?? 0)} loads &middot; {(driverLoadCount[detailTarget.id]?.cy ?? 0).toLocaleString()} CY
                </Text>
              </div>
            </SimpleGrid>

            {detailTarget.notes && (
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Notes</Text>
                <Text size="sm">{detailTarget.notes}</Text>
              </div>
            )}

            {/* Recent loads */}
            <Text size="sm" fw={600} mt="sm">Recent Load Tickets</Text>
            {(() => {
              const iTickets = loadTickets
                .filter(t => t.crewId === String(detailTarget.id))
                .sort((a, b) => (b.id || 0) - (a.id || 0))
                .slice(0, 10);
              if (iTickets.length === 0) return <Text size="sm" c="dimmed">No load tickets yet</Text>;
              return (
                <Table striped highlightOnHover withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Ticket</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th ta="right">CY</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Pickup</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {iTickets.map(t => (
                      <Table.Tr key={t.id}>
                        <Table.Td><Text size="sm" fw={500}>{t.ticketNumber}</Text></Table.Td>
                        <Table.Td><Text size="sm">{t.debrisType}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="sm">{parseFloat(t.calculatedCubicYards || '0').toLocaleString()}</Text></Table.Td>
                        <Table.Td><Badge size="xs">{t.status?.replace(/_/g, ' ')}</Badge></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{t.pickupTime || '-'}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              );
            })()}

            <Group justify="flex-end">
              {canEdit && (
              <Button variant="light" size="xs" onClick={() => { setDetailTarget(null); openEdit(detailTarget); }}>
                Edit Driver
              </Button>
              )}
              <Button variant="default" size="xs" onClick={() => setDetailTarget(null)}>Close</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Driver' : 'Add Driver'} centered size="lg">
        <Stack>
          <Text size="sm" fw={600} c="dimmed">Driver Info</Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <TextInput label="Driver Name" required value={form.driverName ?? ''}
              onChange={e => setField('driverName', e.currentTarget.value)} />
            <TextInput label="Phone" value={form.driverPhone ?? ''}
              onChange={e => setField('driverPhone', e.currentTarget.value)} />
            <TextInput label="Email" value={form.driverEmail ?? ''}
              onChange={e => setField('driverEmail', e.currentTarget.value)} />
          </SimpleGrid>

          <Text size="sm" fw={600} c="dimmed">Assignment</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Crew Name" value={form.crewName ?? ''}
              onChange={e => setField('crewName', e.currentTarget.value)}
              description="Auto-generated if left blank" />
            <Select label="Crew Type" data={CREW_TYPES} value={form.crewType || null}
              onChange={v => setField('crewType', v ?? '')} clearable />
            <Select label="Contractor" data={contractorSelect} value={form.contractorId || null}
              onChange={v => setField('contractorId', v ?? '')} searchable clearable />
            <Select label="Truck" data={truckSelect} value={form.truckId || null}
              onChange={v => setField('truckId', v ?? '')} searchable clearable />
            <Select label="Assigned Zone" data={zoneSelect} value={form.assignedZoneId || null}
              onChange={v => setField('assignedZoneId', v ?? '')} searchable clearable />
            <Select label="Status" data={STATUS_OPTIONS}
              value={form.status ?? 'active'}
              onChange={v => setField('status', v ?? 'active')} />
          </SimpleGrid>

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.currentTarget.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Add Driver'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Remove Driver" centered size="sm">
        <Stack>
          <Text size="sm">Remove driver <strong>{deleteTarget?.driverName}</strong> and their crew assignment?</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Remove</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
